import React, { useState } from 'react';
import {
  Box,
  FlexBox,
  Typography,
  Card,
  Paginator,
  Button,
  AbsoluteBox,
  useTheme,
  useMediaQuery,
  Grid,
  MuiBox
} from '@kis-technologies/kis-ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectors as configSelectors, InfoView, InfoContent } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { selectors as kioskSelectors, Kiosk } from 'services/kiosk';
import { useSelector } from 'react-redux';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import WillCallLogo from 'assets/will_call_icon.png';
import Img from 'react-image';
import get from 'lodash.get';
import { ArrowPagination } from 'components/Pagination';
import { ActionOverlay } from 'components/ActionOverlay';
import { Scrollbar } from 'components/Scrollbar';
import { useStyles } from './styled';
import { useTranslation } from 'react-i18next';

const InfoViewPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  // Action Creators and Selectors
  const loading = useSelector(ticketingSelectors.selectLoading);
  const [isShowMore, setShowMore] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InfoContent | null>(null);
  const configuration = useSelector(configSelectors.selectConfiguration);
  const { label, content }: InfoView = get(configuration, `infoView.${i18n.language}`, { label: '', content: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const { clientId } = kiosk.venue;

  // ***************
  // ** FUNCTIONS **
  // ***************

  const onPrevPageClick = () => {
    // re-routes to the previous page (StartPurchaseView)
    navigate('/ticketing/start-purchase', { state: { from: location.pathname } });
  };

  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('lg'));
  const getActionOverlayHeight = () => {
    let height = 810;
    if (matches) {
      height = 1260;
    }
    return height;
  };

  const renderActionTitle = (item: any) =>
    item && (
      <Grid className={classes.infoRenderActionContainer}>
        <Grid className={classes.infoActionImageContainer}>
          <Grid className={classes.infoActionOverlayImageBlock}>
            <Img
              src={[
                `${process.env.REACT_APP_S3_ASSETS}/${clientId}/info_images/${item.image}`,
                `${process.env.REACT_APP_S3_ASSETS}/${clientId}/info_images/default.png`
              ]}
              unloader={<img src={WillCallLogo} alt="Event Logo" className={classes.infoViewImage} />}
              width="100%"
              height="100%"
              style={{ objectFit: 'cover' }}
            />
          </Grid>
          <MuiBox className={classes.infoViewDividerLandscape}>
            <Card.Divider />
          </MuiBox>
          <Grid className={classes.infoActionOverlayHeaderBlock}>
            <Typography variant="h2">{item.header}</Typography>
            <Typography variant="h5">{item.subHeader}</Typography>
          </Grid>
          <MuiBox className={classes.infoViewDividerPortrait}>
            <Card.Divider />
          </MuiBox>
        </Grid>
        <Grid>
          <MuiBox className={classes.infoViewDescriptionBox}>
            <Scrollbar>
              <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
                <Box flexOverride="none" padding="2">
                  {' '}
                  {item.description}{' '}
                </Box>
              </FlexBox>
            </Scrollbar>
          </MuiBox>
        </Grid>
      </Grid>
    );

  return (
    <>
      {loading && <LoadingOverlay style={{ zIndex: '99' }} />}
      <Grid className={classes.infoViewOuterContainer}>
        <Grid className={classes.infoViewInnerContainer}>
          {/* Title */}
          <Box fullWidth flexOverride="none" margin="0 0 3">
            <Typography variant="h2" className={classes.infoViewHeaderLabel}>
              {label}
            </Typography>
          </Box>

          <MuiBox className={classes.infoViewLandscapeContainer}>
            {/* List of cards, wrapped in paginator. Paginates if there are more than 3 cards */}
            {content && content.length ? (
              <Paginator
                paginationComponent={ArrowPagination}
                items={content}
                itemsPerPage={3}
                page={currentPage}
                onPageChange={(newPage) => setCurrentPage(newPage)}
              >
                {({ items }) => (
                  <FlexBox noWrap justify="around" gutter={4} height={640} padding="0 12">
                    {items.map((item, idx) => (
                      <Box key={idx} maxWidth={420}>
                        {/* Genre Card */}
                        <Card hasBorderRadius raised fullHeight>
                          {/* Selects genre name on click of card */}
                          <Card.ActionArea>
                            <FlexBox height={220} align="center" justify="center" padding="0" overflow="hidden">
                              <Img
                                src={[
                                  `${process.env.REACT_APP_S3_ASSETS}/${clientId}/info_images/${item.image}`,
                                  `${process.env.REACT_APP_S3_ASSETS}/${clientId}/info_images/default.png`
                                ]}
                                unloader={
                                  <img
                                    src={WillCallLogo}
                                    alt="Event Logo"
                                    width="100%"
                                    height="100%"
                                    style={{ objectFit: 'scale-down' }}
                                  />
                                }
                                width="100%"
                                height="100%"
                                style={{ borderRadius: '10px 10px 0 0', objectFit: 'cover' }}
                              />
                            </FlexBox>

                            <FlexBox flexOverride="0 1 0%" padding="3 7 3" direction="column">
                              <Box flexOverride="none" margin="0 0 2">
                                <Typography variant="h5" sx={{ lineHeight: 1.1 }}>
                                  {item.header}
                                </Typography>
                              </Box>
                              <Box maxHeight={15} padding="1 0 4">
                                <Card.Divider />
                              </Box>
                              <Box flexOverride="none">
                                <Typography
                                  sx={{ lineHeight: '1.78', fontSize: '18px', overflow: 'hidden', height: '240px' }}
                                >
                                  {item.description}
                                </Typography>
                              </Box>
                              <AbsoluteBox
                                bottom={0}
                                left={0}
                                fullWidth
                                sx={{ backgroundImage: 'linear-gradient(to top, #fff 75%, #fff0 100%)' }}
                                padding="8 8 6"
                              >
                                <Button
                                  customSize="large"
                                  appearance="primary"
                                  fullWidth
                                  onClick={() => {
                                    setSelectedItem(item);
                                    setShowMore(true);
                                  }}
                                >
                                  <Typography variant="h6">{t('info.readMore')}</Typography>
                                </Button>
                              </AbsoluteBox>
                            </FlexBox>
                          </Card.ActionArea>
                        </Card>
                      </Box>
                    ))}
                  </FlexBox>
                )}
              </Paginator>
            ) : (
              <FlexBox fullWidth fullHeight align="center" justify="center">
                <Typography align="center" variant="h4">
                  {t('info.nothingToDisplay')} <br /> {t('info.tryAfterSomeTime')}
                </Typography>
              </FlexBox>
            )}
          </MuiBox>
          <MuiBox className={classes.infoViewPortraitContainer}>
            {content && content.length ? (
              <Scrollbar>
                <FlexBox direction="row" align="stretch" padding="0 4 7 0" margin="0 0 4" noWrap flexOverride="1">
                  <FlexBox direction="column" gutter={6} noWrap fullWidth flexOverride="1">
                    {content.map((item, id) => (
                      <Box key={id}>
                        {/* Genre Card */}
                        <Card hasBorderRadius raised fullHeight>
                          {/* Selects genre name on click of card */}
                          <Card.ActionArea>
                            <Grid container xl={6} lg={6}>
                              <Grid item className={classes.infoPortraitImageBlock}>
                                <Img
                                  src={[
                                    `${process.env.REACT_APP_S3_ASSETS}/${clientId}/info_images/${item.image}`,
                                    `${process.env.REACT_APP_S3_ASSETS}/${clientId}/info_images/default.png`
                                  ]}
                                  unloader={
                                    <img
                                      src={WillCallLogo}
                                      alt="Event Logo"
                                      width="100%"
                                      height="100%"
                                      style={{ objectFit: 'scale-down' }}
                                    />
                                  }
                                  width="100%"
                                  height="100%"
                                  style={{ borderRadius: '10px 10px 0 0', objectFit: 'cover' }}
                                />
                              </Grid>

                              <Grid item className={classes.infoPortraitTextBlock}>
                                <Box flexOverride="none" margin="7 0 2">
                                  <Typography variant="h5" sx={{ lineHeight: 1.1 }}>
                                    {item.header}
                                  </Typography>
                                </Box>
                                <Box maxHeight={15} padding="1 0 4">
                                  <Card.Divider />
                                </Box>
                                <Box flexOverride="none">
                                  <Typography
                                    sx={{
                                      lineHeight: '1.78',
                                      fontSize: '18px',
                                      overflow: 'hidden',
                                      height: '240px'
                                    }}
                                  >
                                    {item.description}
                                  </Typography>
                                </Box>
                                <AbsoluteBox
                                  bottom={0}
                                  left={0}
                                  fullWidth
                                  sx={{
                                    backgroundImage: 'linear-gradient(to top, #fff 75%, #fff0 100%)',
                                    position: 'relative'
                                  }}
                                  padding="8 8 6"
                                >
                                  <Button
                                    customSize="large"
                                    appearance="primary"
                                    fullWidth
                                    onClick={() => {
                                      setSelectedItem(item);
                                      setShowMore(true);
                                    }}
                                  >
                                    <Typography variant="h6">{t('info.readMore')}</Typography>
                                  </Button>
                                </AbsoluteBox>
                              </Grid>
                            </Grid>
                          </Card.ActionArea>
                        </Card>
                      </Box>
                    ))}
                  </FlexBox>
                </FlexBox>
              </Scrollbar>
            ) : (
              <FlexBox fullWidth fullHeight align="center" justify="center">
                <Typography align="center" variant="h4">
                  {t('info.nothingToDisplay')} <br /> {t('info.tryAfterSomeTime')}
                </Typography>
              </FlexBox>
            )}
          </MuiBox>
        </Grid>
      </Grid>
      {/* Footer */}
      <Footer onPrevClick={onPrevPageClick} />

      {/* actionoverlay when read more selected*/}
      {isShowMore && selectedItem && (
        <ActionOverlay
          title=""
          description={renderActionTitle(selectedItem)}
          confirmText={t('commonText.close')}
          onConfirm={() => {
            setShowMore(false);
          }}
          height={getActionOverlayHeight()}
          width={1480}
        />
      )}
    </>
  );
};

export default InfoViewPage;
